/** `DISABLED`: cuenta desvinculada desde Admin; ya no recibe cobros (como si no tuviera Fina Pago). */
export type FinaPaymentsSettingsStatus = 'VERIFYING' | 'ACTIVE' | 'FAILED' | 'UNCERTAIN' | 'REPLACED' | 'DISABLED';
export type FinaPaymentStatus = 'PENDING' | 'PAID' | 'ACCREDITED' | 'FAILED' | 'EXPIRED' | 'CANCELLED';
/** Origen del cobro: venta del POS, cuenta por cobrar o QR fijo del comercio (sin venta hasta que el cajero lo aplica). */
export type FinaPaymentSource = 'POS' | 'CxC' | 'QR';
export type FinaPaymentsDestinationType = 'phone' | 'account';

/** `GET /api/payments/settings` → `settings` (sin secretos: el destino viene enmascarado). */
export interface FinaPaymentsSettings {
    /** Id de la fila (`PaymentsSettings`): una por cuenta del comercio. */
    _id: string;
    status: FinaPaymentsSettingsStatus;
    bankAccountId: string;
    paymentMethodId: string | null;
    destinationType: FinaPaymentsDestinationType;
    destinationHint: string;
    /** Comisión de Fina (%) vigente para este comercio; la cambia el owner. */
    feePercent: number;
    feeHistory: Array<{ percent: number; previousPercent: number | null; changedBy: string | null; changedAt: string }>;
    bankCode: string | null;
    identification: string;
    /** Titular escrito en el modal de activación; el modal lo precarga en la siguiente. */
    holderName?: string | null;
    holderKind?: 'j' | 'v' | null;
    userType: string;
    lastErrorMessage: string | null;
    requestedAt: string;
    verifiedAt: string | null;
    replacedAt: string | null;
    /** La cuenta que recibe los cobros del comercio. */
    isDefault?: boolean;
    /** Cuenta adicional con OTP pendiente. */
    verificationExpiresAt?: string | null;
    verificationPhoneHint?: string | null;
    otpAttempts?: number;
    /** `integrator`: cuenta de validación creada con la sesión de Fina en el proveedor (no recibe cobros del comercio). */
    providerAccountScope?: 'merchant' | 'integrator' | null;
    /** `partner` (receptor, una cuenta fija) o `associated` (comercio integrado: credenciales propias, varias cuentas). */
    providerModel?: 'partner' | 'associated';
    /** Comercio integrado: `pending_verification` hasta completar la verificación bancaria inicial. */
    providerStatus?: 'pending_verification' | 'active' | null;
    /** Verificación bancaria inicial pendiente: `otp` (persona natural, SMS) o `amounts` (jurídica, dos microabonos). */
    verificationKind?: 'otp' | 'amounts' | null;
    /** Intentos que quedan en la verificación en curso (al agotarse hay que empezar de nuevo). */
    attemptsLeft?: number;
    /** `false` mientras el comercio no pueda cobrar todavía (receptor en configuración). */
    recipientReady?: boolean;
}

export interface FinaPaymentsSettingsResponse {
    settings: FinaPaymentsSettings | null;
    history: FinaPaymentsSettings[];
    /** Cuentas del comercio en Fina Pago: activas (la predeterminada primero) y adicionales con OTP pendiente. */
    accounts?: FinaPaymentsSettings[];
    /** ¿El proveedor permite asociar más de una cuenta? */
    multipleAccountsSupported?: boolean;
    /** ¿Se puede cambiar la cuenta predeterminada en el proveedor? */
    canSetDefaultAccount?: boolean;
    termsUrl: string;
    provider: 'mock' | 'live';
    /** Comisión con la que arranca una activación nueva (la fija el ambiente). */
    defaultFeePercent: number;
    /** Datos de pago móvil para los clientes del comercio (pantalla «¡Listo!»); `null` si faltan. */
    mobilePaymentData?: FinaPagoMobilePaymentData | null;
}

export interface FinaPagoMobilePaymentData {
    bank: string;
    phone: string;
    rif: string;
    /** QR bancario estático (sin monto) con la cuenta receptora: lo lee la app del banco del cliente. */
    qrPayload?: string | null;
}

/** QR bancario con la cuenta receptora y el monto exacto de un cobro de pago móvil (lo manda el api mientras está pendiente). */
export interface FinaPagoMobileQr {
    payload: string;
    bankCode: string;
    bankName: string;
    validationStatus: string;
}

export interface FinaPaymentsActivationRequest {
    bankAccountId: string;
    destinationType: FinaPaymentsDestinationType;
    destination: string;
    bankCode?: string;
    /** Documento del titular escrito en la tarjeta de la cuenta (obligatorio para el api). */
    identification?: string;
    /** Nombre o razón social del titular. */
    holderName?: string;
    /** Persona jurídica (`j`) o natural (`v`). */
    holderKind?: 'j' | 'v';
    acceptedTerms: boolean;
    replace?: boolean;
    /** Cuenta adicional: celular que recibe el código del banco (obligatorio con número de cuenta). */
    verificationPhone?: string;
    entryPoint?: 'banner' | 'card' | 'pos' | 'topbar' | 'promo';
}

export interface FinaPaymentsActivationResponse {
    title: string;
    message: string;
    settings: FinaPaymentsSettings;
}

export interface FinaPaymentsChargeRequest {
    /** Cuenta elegida en el POS (fila de `accounts`); si no es la predeterminada, el api la vuelve predeterminada. */
    paymentsSettingsId?: string;
    /** Venta abierta del POS o cuenta por cobrar pendiente: exactamente uno. */
    saleId?: string;
    pendingAccountId?: string;
    /** «Cobrar todo»: grupo de cuentas por cobrar del mismo cliente. */
    pendingAccountIds?: string[];
    amountVES: number;
    amountUSD: number;
    exchangeRate: number;
    idempotencyKey: string;
    description?: string;
    /** Método elegido en el POS: el api abre la sesión con el acuerdo que solo admite ese método. */
    method?: 'immediate_debit' | 'mobile_payment';
    /** Cliente de la venta / cuenta: el api lo guarda y precarga la página del pagador (QR y enlace). */
    payerPrefill?: FinaPagoPayerPrefill;
}

/** `payment` de `/api/payments/charges*`. */
export interface FinaPayment {
    paymentId: string;
    status: FinaPaymentStatus;
    source: FinaPaymentSource;
    saleId: string | null;
    paymentMethodId: string | null;
    bankAccountId: string;
    amountVES: number;
    amountUSD: number;
    exchangeRate: number;
    tenantShareVES: number;
    finaShareVES: number;
    paymentUrl: string | null;
    /** Quién sirve `paymentUrl`: `fina` = página del pagador de Fina (embebible en el modal), `provider` = página del proveedor. */
    payerPage?: 'fina' | 'provider';
    expiresAt: string | null;
    paidAt: string | null;
    accreditedAt: string | null;
    closedAt: string | null;
    paymentMethod: string | null;
    /** Método con el que se pidió el cobro (`immediate_debit` | `mobile_payment`); el real llega en `paymentMethod`. */
    requestedMethod?: string | null;
    bankReferenceId: string | null;
    createdAt: string;
    /** QR fijo que originó el cobro (solo `source: 'QR'`). */
    staticQrId?: string | null;
    /** Reserva del cobro QR por una caja (15 min) mientras cierra su venta. */
    claimedAt?: string | null;
    claimedBy?: string | null;
    claimedSaleId?: string | null;
    /** Cédula del pagador (`V-20362352`) para la bandeja «Cobros recibidos». */
    payerHint?: string | null;
    /** Nombre que escribió el pagador en la pantalla del QR fijo. */
    payerName?: string | null;
    /** QR fijo con pago móvil: referencia del comprobante que reportó el cliente («Ya pagué»). */
    payerReference?: string | null;
    /** Pago móvil pendiente: QR que la app del banco del cliente lee con cuenta receptora y monto. */
    mobileQr?: FinaPagoMobileQr | null;
}

/** QR fijo del comercio (`GET/POST /api/payments/qr`). */
export interface FinaPaymentsStaticQr {
    code: string;
    /** URL impresa en el QR (`/pay/m/<code>`). */
    url: string;
    status: 'ACTIVE' | 'REVOKED';
    createdAt: string;
}

/** Payload del socket `payment_claimed`: otra caja reservó el cobro QR. */
export interface FinaPaymentClaimedEvent {
    paymentId: string;
    claimedBy: string;
    claimedSaleId: string;
    claimedAt: string;
}

/** Payload del socket `payment_confirmed` (api: `emitToTenant`). */
export interface FinaPaymentConfirmedEvent {
    paymentId: string;
    saleId: string | null;
    status: FinaPaymentStatus;
    amountVES: number;
    paidAt: string | null;
}

/**
 * Datos del cliente de la venta para precargar el «Débito inmediato» embebido (los manda el modal a
 * la página del pagador por postMessage; nunca van en la URL). Solo lo que admite el SDK de Spidi:
 * cédula V/E/P numérica y teléfono 04xx + 7 dígitos. El banco lo elige el cajero.
 */
export interface FinaPagoPayerPrefill {
    identificationType: 'V' | 'E' | 'P';
    identification: string;
    phonePrefix: string;
    phone: string;
}

/** Arma el precargado desde el cliente del POS; `null` si no hay nada utilizable. */
export function buildFinaPagoPayerPrefill(customer: { dniType?: string | null; dniNumber?: string | null; phone?: string | null } | null | undefined): FinaPagoPayerPrefill | null {
    if (!customer) return null;
    const type = String(customer.dniType || '').toUpperCase();
    const dniDigits = String(customer.dniNumber || '').replace(/\D/g, '');
    const identification = ['V', 'E', 'P'].includes(type) && /^\d{1,9}$/.test(dniDigits) ? dniDigits : '';

    let digits = String(customer.phone || '').replace(/\D/g, '');
    if (digits.startsWith('58') && digits.length === 12) digits = `0${digits.slice(2)}`;
    if (digits.length === 10 && digits.startsWith('4')) digits = `0${digits}`;
    const validPhone = /^04(12|14|16|22|24|26)\d{7}$/.test(digits);

    const prefill: FinaPagoPayerPrefill = {
        identificationType: identification ? (type as 'V' | 'E' | 'P') : 'V',
        identification,
        phonePrefix: validPhone ? digits.slice(0, 4) : '',
        phone: validPhone ? digits.slice(4) : '',
    };
    return prefill.identification || prefill.phone ? prefill : null;
}

/** Lo que el modal del POS devuelve al panel de pago cuando el cobro quedó pagado. */
export interface FinaPaymentsModalResult {
    paymentId: string;
    paymentMethodId: string | null;
    amountVES: number;
    amountUSD: number;
    bankReferenceId: string | null;
}

export const FINA_PAYMENTS_PAYMENT_METHOD_TYPE_CODE = 'FINA-PAYMENTS';
/** Nombre con el que el API crea el método de pago en la BD (no es copy: la marca visible es «Fina Pago»). */
export const FINA_PAYMENTS_PAYMENT_METHOD_NAME = 'Fina Payments';
export const FINA_PAGO_DISPLAY_NAME = 'Fina Pago';

/**
 * Opción «Fina Pago · Pago móvil» en el POS (botón, pago combinado e inicial de Cashea). Encendida en
 * esta rama para probar el pago móvil «sin pre-registro» del SDK 0.3.0 (el cliente transfiere el monto
 * exacto y luego «Ya pagué»); el release a producción sale solo con débito inmediato (`false`).
 */
export const FINA_PAGO_MOBILE_PAYMENT_ENABLED = true;

/**
 * Bandejas del QR fijo en el POS («Cobros recibidos» y «Pago móvil (nuevo)»). Apagadas mientras se
 * prueba el pago móvil desde el botón del POS: el cobro nace de la venta y no hace falta aplicarlo.
 */
export const FINA_PAGO_QR_TRAY_ENABLED = false;

/** ¿La línea de pago (venta, CxC o resumen agrupado por nombre) es de Fina Pago? */
export function isFinaPagoPaymentLine(pm: any): boolean {
    if (!pm) return false;
    if (pm.finaPaymentId || pm.type?.code === FINA_PAYMENTS_PAYMENT_METHOD_TYPE_CODE) return true;
    // Nombre de BD («Fina Payments») o el de marca si el comercio renombró el método («Fina Pago»).
    const name = String(pm.name || '')
        .trim()
        .toLowerCase();
    return name === FINA_PAYMENTS_PAYMENT_METHOD_NAME.toLowerCase() || name === FINA_PAGO_DISPLAY_NAME.toLowerCase();
}

/**
 * Cómo pagó el cliente según el proveedor (`payment.paymentMethod`). El método no se elige al crear el
 * cobro: el cliente lo escoge en la página de pago (débito inmediato con clave, o pago móvil si la
 * clave no le llega), y Spidi lo informa al confirmar.
 */
export function finaPaymentMethodLabel(code: string | null | undefined): string {
    const c = String(code || '').toLowerCase();
    if (c === 'immediate_debit') return 'Débito inmediato';
    if (c === 'mobile_payment') return 'Pago móvil';
    if (c === 'crypto') return 'Cripto';
    return '';
}

/** Etiqueta de un cobro histórico de Fina Pago con el flag `finapago` apagado: sin la marca. */
export const FINA_PAGO_NEUTRAL_LABEL = 'Pago electrónico';

/** «Débito inmediato» / «Pago móvil» si se sabe cómo pagó el cliente; si no, «Pago electrónico». */
export function finaPagoNeutralLabel(providerMethod?: string | null): string {
    return finaPaymentMethodLabel(providerMethod) || FINA_PAGO_NEUTRAL_LABEL;
}

/**
 * Nombre visible del método: la BD guarda «Fina Payments», el comercio ve «Fina Pago». Con el flag
 * `finapago` apagado (`finaEnabled: false`) el cobro sigue visible pero sin la marca: se muestra el
 * método real (`providerMethod`) o «Pago electrónico».
 */
export function paymentLineDisplayName(pm: any, opts: { finaEnabled?: boolean; providerMethod?: string | null } = {}): string {
    if (!isFinaPagoPaymentLine(pm)) return String(pm?.name || '');
    return opts.finaEnabled === false ? finaPagoNeutralLabel(opts.providerMethod) : FINA_PAGO_DISPLAY_NAME;
}
